import { apiRequest } from "./apiClient";
import { isDataSyncConnected } from "./dataSyncService";
import { readSensitiveItem, writeSensitiveItem } from "../utils/sensitiveSessionStorage";

export const ACTIVITY_CATEGORIES = [
  "all",
  "shopping",
  "tasks",
  "calendar",
  "finance",
  "documents",
  "health",
  "children",
  "chat",
  "management",
];

export const activityCategoryMeta = {
  shopping: { icon: "🛒", page: "alisveris" },
  tasks: { icon: "✅", page: "gorev" },
  calendar: { icon: "📅", page: "takvim" },
  finance: { icon: "💳", page: "finans" },
  documents: { icon: "📁", page: "belgeler" },
  health: { icon: "❤️", page: "saglik" },
  children: { icon: "👶", page: "cocuk" },
  chat: { icon: "💬", page: "chat" },
  management: { icon: "🔐", page: "kullaniciYonetimi" },
  system: { icon: "✨", page: "ana" },
};

// Aktivite akışı için polling aralığı (ms)
const ACTIVITY_POLL_INTERVAL = 30000;

const ACTIVITY_CACHE_PREFIX = "familyPlanner.activityFeed.v1";
const ACTIVITY_CACHE_MAX_AGE_MS = 1000 * 60 * 60 * 24;

function activityCacheKey(familyId) {
  return `${ACTIVITY_CACHE_PREFIX}.${familyId}`;
}

export function getCachedActivityFeed(familyId, maxItems = 120) {
  const cached = readActivityCache(familyId);
  return cached ? cached.slice(0, maxItems) : null;
}

function readActivityCache(familyId) {
  if (!familyId || typeof window === "undefined") return null;
  try {
    const raw = readSensitiveItem(activityCacheKey(familyId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.familyId !== familyId || !Array.isArray(parsed.activities)) return null;
    if (Date.now() - Number(parsed.savedAt || 0) > ACTIVITY_CACHE_MAX_AGE_MS) return null;
    return parsed.activities;
  } catch {
    return null;
  }
}

function writeActivityCache(familyId, activities) {
  if (!familyId || typeof window === "undefined" || !Array.isArray(activities)) return;
  try {
    writeSensitiveItem(activityCacheKey(familyId), JSON.stringify({
      familyId,
      savedAt: Date.now(),
      activities: activities.slice(0, 120),
    }));
  } catch {
    // localStorage dolu/kapalı olabilir; akışı bozma.
  }
}


const safeText = (value) => String(value || "").trim();

export const getActivityActor = ({ user, name, role, avatar } = {}) => ({
  uid: user?.uid || "",
  name: safeText(name) || user?.displayName || "",
  role: role || "",
  avatar: avatar || null,
});

/* ── Aktivite oluştur ── */
export async function createActivity({
  familyId,
  type,
  category,
  actor,
  payload = {},
  targetPage = "",
  targetId = "",
  importance = "normal",
  visibility = "family",
}) {
  if (!familyId || !type || !category) return null;

  try {
    const meta = activityCategoryMeta[category] || activityCategoryMeta.system;
    const res = await apiRequest(`/activity/${familyId}`, {
      method: "POST",
      body: JSON.stringify({
        type,
        category,
        actorId: actor?.uid || "",
        actorName: actor?.name || "",
        actorRole: actor?.role || "",
        actorAvatar: actor?.avatar || null,
        payload,
        targetPage: targetPage || meta.page || "",
        targetId: targetId || "",
        importance,
        visibility,
      }),
    });
    return res?.activity?.id ?? null;
  } catch (error) {
    console.warn("Aktivite kaydı oluşturulamadı:", error);
    return null;
  }
}

/* ── Aktivite akışını dinle (polling tabanlı, eski imza korunuyor) ── */
export function subscribeActivityFeed(familyId, callback, maxItems = 50) {
  if (!familyId) return () => {};

  let cancelled = false;
  let hasDeliveredData = false;
  const cached = readActivityCache(familyId);

  if (cached) {
    hasDeliveredData = true;
    callback(cached.slice(0, maxItems));
  }

  const fetchFeed = async () => {
    // Arka plandayken aktivite akışı polling'i atlanır; öne dönünce tazelenir.
    if (typeof document !== "undefined" && document.hidden) return;
    try {
      const res = await apiRequest(`/activity/${familyId}?limit=${maxItems}`);
      const nextActivities = res?.activities || [];
      writeActivityCache(familyId, nextActivities);
      if (!cancelled) {
        hasDeliveredData = true;
        callback(nextActivities);
      }
    } catch (error) {
      console.warn("Aktivite akışı alınamadı:", error);
      if (!cancelled && !hasDeliveredData) callback([]);
    }
  };

  const onDataChanged = (event) => {
    const detail = event?.detail || {};
    if (detail.familyId !== familyId || detail.resource !== "activity") return;

    const current = readActivityCache(familyId) || [];
    let next = current;
    if ((detail.action === "added" || detail.action === "updated") && detail.item?.id) {
      const exists = current.some((item) => item.id === detail.item.id);
      next = exists
        ? current.map((item) => item.id === detail.item.id ? { ...item, ...detail.item } : item)
        : [detail.item, ...current];
    } else if (detail.action === "deleted" && detail.itemId) {
      next = current.filter((item) => item.id !== detail.itemId);
    } else {
      // Eksik/gelecekteki bir event şekli gelirse doğruluğu tam snapshot ile koru.
      fetchFeed();
      return;
    }
    next = next.slice(0, Math.max(120, maxItems));
    writeActivityCache(familyId, next);
    if (!cancelled) callback(next.slice(0, maxItems));
  };

  fetchFeed();
  // SSE bağlıyken interval yalnız ucuz bir bağlantı kontrolüdür; REST üretmez.
  // Eski backend/SSE kopması halinde mevcut 30 sn güvenlik davranışı korunur.
  const interval = setInterval(() => {
    if (!isDataSyncConnected(familyId)) fetchFeed();
  }, ACTIVITY_POLL_INTERVAL);
  const onVisibleActivity = () => { if (!document.hidden) fetchFeed(); };
  document.addEventListener("visibilitychange", onVisibleActivity);
  window.addEventListener("fp:dataChanged", onDataChanged);

  return () => {
    cancelled = true;
    clearInterval(interval);
    document.removeEventListener("visibilitychange", onVisibleActivity);
    window.removeEventListener("fp:dataChanged", onDataChanged);
  };
}

/* ── Okundu işaretle ── */
export async function markActivityRead({ familyId, activityId, uid }) {
  if (!familyId || !activityId || !uid) return;
  try {
    await apiRequest(`/activity/${familyId}/${activityId}/read`, {
      method: "PATCH",
      body: JSON.stringify({ uid }),
    });
  } catch (error) {
    console.warn("Aktivite okundu işaretlenemedi:", error);
  }
}

/* ── Geriye dönük uyumluluk ──
   Backend POST /activity/:familyId zaten ekleme sonrası otomatik
   temizlik yapıyor (ACTIVITY_LIMIT / ACTIVITY_MAX_DAYS). Bu fonksiyon
   artık no-op; eski importlar kırılmasın diye export korunuyor. */
export async function cleanupActivityFeed() {
  return;
}

export function canViewActivity(activity, permissions, currentUid) {
  if (!activity) return false;
  if (activity.visibility === "private") return activity.actorId === currentUid;
  if (activity.visibility === "admins") return permissions?.pages?.userManagement === true;

  const categoryToPage = {
    shopping: "shopping",
    tasks: "tasks",
    calendar: "calendar",
    finance: "finance",
    documents: "documents",
    health: "health",
    children: "children",
    chat: "chat",
    management: "userManagement",
  };

  const pageKey = categoryToPage[activity.category];
  if (!pageKey) return true;
  return permissions?.pages?.[pageKey] !== false;
}

/* ── Tarih ayrıştırma — Firestore Timestamp VEYA ISO string/Date destekler ── */
function parseActivityDate(createdAt) {
  const date =
    createdAt?.toDate?.() ||
    (createdAt?.seconds ? new Date(createdAt.seconds * 1000) : null) ||
    (createdAt ? new Date(createdAt) : null);

  if (!date || Number.isNaN(date.getTime())) return null;
  return date;
}

export function activityTimeLabel(createdAt, lang = "tr") {
  const date = parseActivityDate(createdAt);
  if (!date) return lang === "en" ? "Just now" : "Az önce";

  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.max(0, Math.floor(diffMs / 60000));
  if (diffMin < 1) return lang === "en" ? "Just now" : "Az önce";
  if (diffMin < 60) return lang === "en" ? `${diffMin} min ago` : `${diffMin} dk önce`;

  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return lang === "en" ? `${diffHour} h ago` : `${diffHour} sa önce`;

  const diffDay = Math.floor(diffHour / 24);
  if (diffDay === 1) return lang === "en" ? "Yesterday" : "Dün";
  if (diffDay < 7) return lang === "en" ? `${diffDay} days ago` : `${diffDay} gün önce`;

  return date.toLocaleDateString(lang === "en" ? "en-US" : "tr-TR", {
    day: "2-digit",
    month: "short",
    year: date.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
  });
}

export function activityDateGroup(createdAt, lang = "tr") {
  const date = parseActivityDate(createdAt);
  if (!date) return lang === "en" ? "Today" : "Bugün";

  const start = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const today = start(new Date());
  const target = start(date);
  const diffDay = Math.floor((today - target) / 86400000);

  if (diffDay <= 0) return lang === "en" ? "Today" : "Bugün";
  if (diffDay === 1) return lang === "en" ? "Yesterday" : "Dün";
  if (diffDay < 7) return lang === "en" ? "This Week" : "Bu Hafta";
  return lang === "en" ? "Older" : "Daha Eski";
}

export function buildActivityText(activity, t, lang = "tr") {
  const p = activity?.payload || {};
  const actor = activity?.actorName || (lang === "en" ? "A family member" : "Bir aile üyesi");
  const rawItem = p.itemName || p.taskName || p.eventTitle || p.title || p.documentName || p.recordName || p.memberName || "";
  const legacyFinanceKeys = {
    "finance.savings":"finance.tab.savings",
    "finance.installments":"finance.tab.installments",
    "finance.subscriptions":"finance.tab.subscriptions",
    "finance.bills":"finance.tab.bills",
  };
  const item = legacyFinanceKeys[rawItem] ? t(legacyFinanceKeys[rawItem]) : rawItem;
  const amount = p.amount ? ` · ${p.amount}` : "";

  const fallback = {
    title: lang === "en" ? "Family activity" : "Aile aktivitesi",
    message: lang === "en" ? `${actor} made an update.` : `${actor} bir güncelleme yaptı.`,
  };

  const map = {
    shopping_item_added: {
      title: t("activity.title.shoppingAdded"),
      message: t("activity.msg.shoppingAdded", { actor, item }),
    },
    shopping_item_completed: {
      title: t("activity.title.shoppingCompleted"),
      message: t("activity.msg.shoppingCompleted", { actor, item }),
    },
    shopping_item_deleted: {
      title: t("activity.title.shoppingDeleted"),
      message: t("activity.msg.shoppingDeleted", { actor, item }),
    },
    task_added: {
      title: t("activity.title.taskAdded"),
      message: t("activity.msg.taskAdded", { actor, item }),
    },
    task_completed: {
      title: t("activity.title.taskCompleted"),
      message: t("activity.msg.taskCompleted", { actor, item }),
    },
    task_deleted: {
      title: t("activity.title.taskDeleted"),
      message: t("activity.msg.taskDeleted", { actor, item }),
    },
    calendar_event_added: {
      title: t("activity.title.calendarAdded"),
      message: t("activity.msg.calendarAdded", { actor, item }),
    },
    calendar_event_updated: {
      title: t("activity.title.calendarUpdated"),
      message: t("activity.msg.calendarUpdated", { actor, item }),
    },
    calendar_event_deleted: {
      title: t("activity.title.calendarDeleted"),
      message: t("activity.msg.calendarDeleted", { actor, item }),
    },
    finance_added: {
      title: p.financeType === "Gelir" || p.financeType === "income"
        ? t("activity.title.incomeAdded")
        : (p.financeType === "Gider" || p.financeType === "expense" ? t("activity.title.expenseAdded") : t("activity.title.financeAdded")),
      message: t("activity.msg.financeAdded", { actor, item, amount }),
    },
    finance_deleted: {
      title: t("activity.title.financeDeleted"),
      message: t("activity.msg.financeDeleted", { actor, item }),
    },
    document_added: {
      title: t("activity.title.documentAdded"),
      message: t("activity.msg.documentAdded", { actor, item }),
    },
    document_deleted: {
      title: t("activity.title.documentDeleted"),
      message: t("activity.msg.documentDeleted", { actor, item }),
    },
    health_added: {
      title: t("activity.title.healthAdded"),
      message: t("activity.msg.healthAdded", { actor, item }),
    },
    child_record_added: {
      title: t("activity.title.childAdded"),
      message: t("activity.msg.childAdded", { actor, item }),
    },
    family_notification_sent: {
      title: t("activity.title.managementNotification"),
      message: t("activity.msg.managementNotification", { actor, item }),
    },
  };

  return map[activity?.type] || fallback;
}
